// src/auth/PermissionsContext.jsx
import React, { createContext, useMemo, useContext } from "react";
import { useAuthContext } from "./useAuthContext";

const PermissionsContext = createContext(null);

const toSet = (arr) => new Set(Array.isArray(arr) ? arr.filter(Boolean) : []);

/**
 * Normalize abilities - تحويل abilities للصيغة المتوافقة
 * @param {Array} abilities - abilities من الـ API
 * @returns {Set} - Set of normalized permissions
 */
/**
 * Normalize permission names to a standard format (lowercase with underscores)
 * @param {string} name 
 * @returns {string}
 */
const normalizeName = (name) => {
    if (typeof name !== 'string') return '';
    return name.toLowerCase().replace(/[-.:]/g, '_');
};

/**
 * Normalize abilities - تحويل abilities للصيغة المتوافقة
 * @param {Array} abilities - abilities من الـ API
 * @returns {Set} - Set of normalized permissions
 */
const normalizeAbilities = (abilities = []) => {
    const abilitiesArray = Array.isArray(abilities) ? abilities : [];

    // Extract names if they are objects, and ensure we have a flat array of strings
    const flatAbilities = abilitiesArray.map(item => {
        if (typeof item === 'object' && item !== null && item.name) {
            return item.name;
        }
        return item;
    }).filter(item => typeof item === 'string');

    // إذا كان admin:* موجود، يعني لديه كل الصلاحيات
    if (flatAbilities.includes('admin:*') || flatAbilities.includes('*')) {
        return new Set(['*']); // كل الصلاحيات
    }

    // تحويل abilities لصيغة موحدة
    const normalized = flatAbilities.map(ability => normalizeName(ability));

    return toSet(normalized);
};

export function PermissionsProvider({ children }) {
    const { user, isAuthenticated, isInitialized } = useAuthContext();

    // استخدام abilities من user object (في الذاكرة)
    // سيتم ملؤها من localStorage في JwtContext.js
    const abilities = useMemo(() => {
        // محاولة الحصول على abilities من user object أولاً
        if (user?.abilities || user?.permissions) {
            return user.abilities || user.permissions;
        }

        // أو من localStorage كخطة احتياطية
        try {
            const storedAbilities = localStorage.getItem('abilities');
            return storedAbilities ? JSON.parse(storedAbilities) : [];
        } catch {
            return [];
        }
    }, [user]);

    const permissionsSet = useMemo(() => normalizeAbilities(abilities), [abilities]);

    const api = useMemo(() => {
        const checkSingle = (perm) => {
            if (permissionsSet.has('*')) return true;
            
            const normalized = normalizeName(perm);
            if (permissionsSet.has(normalized)) return true;
            
            // التعامل مع الجمع والمفرد (users vs user)
            // إذا كان المطلوب 'view_user' والـ Set فيه 'view_users'
            const plural = normalized.replace(/(_|$)/g, 's$1');
            if (permissionsSet.has(plural)) return true;

            // إذا كان المطلوب 'view_users' والـ Set فيه 'view_user'
            const singular = normalized.replace(/s(_|$)/g, '$1');
            if (permissionsSet.has(singular)) return true;

            // التعامل مع الترتيب المعكوس (action_resource vs resource_action)
            if (normalized.includes('_')) {
                const parts = normalized.split('_');
                // نكتفي بدعم جزئين فقط حالياً لتجنب التعقيد
                if (parts.length === 2) {
                    const reversed = `${parts[1]}_${parts[0]}`;
                    if (permissionsSet.has(reversed)) return true;
                    
                    const reversedPlural = reversed.replace(/(_|$)/g, 's$1');
                    if (permissionsSet.has(reversedPlural)) return true;
                    
                    const reversedSingular = reversed.replace(/s(_|$)/g, '$1');
                    if (permissionsSet.has(reversedSingular)) return true;
                }
            }

            return false;
        };

        const has = (perm) => checkSingle(perm);

        const hasAny = (perms = []) => {
            if (permissionsSet.has('*')) return true;
            return perms.some((p) => checkSingle(p));
        };

        const hasAll = (perms = []) => {
            if (permissionsSet.has('*')) return true;
            return perms.every((p) => checkSingle(p));
        };

        const hasPrefix = (prefix) => {
            if (permissionsSet.has('*')) return true;
            const cleanPrefix = normalizeName(prefix).replace(/_$/, '');
            
            for (const p of permissionsSet) {
                if (p.startsWith(cleanPrefix) || p.endsWith(`_${cleanPrefix}`) || p.includes(`_${cleanPrefix}_`)) {
                    return true;
                }
                // التحويل للمفرد/الجمع في الـ prefix أيضاً
                const singularPrefix = cleanPrefix.replace(/s$/, '');
                if (p.includes(singularPrefix)) return true;
            }
            return false;
        };

        // helper عام: can("countries", "create") => "countries_create"
        const can = (resource, action) => has(`${resource}_${action}`);

        return {
            isInitialized,
            isAuthenticated,
            permissionsSet,
            abilities, // raw abilities
            has,
            hasAny,
            hasAll,
            hasPrefix,
            can,
            user,
        };
    }, [permissionsSet, abilities, isAuthenticated, isInitialized, user]);

    return <PermissionsContext.Provider value={api}>{children}</PermissionsContext.Provider>;
}

export function usePermissions() {
    const ctx = useContext(PermissionsContext);
    if (!ctx) throw new Error("usePermissions must be used within PermissionsProvider");
    return ctx;
}
